import React, { useState, useEffect } from 'react';
import { Container, Modal, Button, Alert, Spinner } from 'react-bootstrap';
import ServerStatus from '../components/organ-segmentation/ServerStatus';
import OrganInput from '../components/organ-segmentation/OrganInput';
import ProgressIndicator from '../components/organ-segmentation/ProgressIndicator';
import StackViewer from '../components/organ-segmentation/StackViewer';
import MeshViewer from '../components/organ-segmentation/MeshViewer';
import ReportInaccuraciesButton from '../components/ReportInaccuraciesButton';
import TotalSegmentatorImage from '../assets/TotalSegmentatorImage.png';
import { 
  APIOrganSegGetStatus, 
  APIOrganSegSegment, 
  APIOrganSegGetSlices, 
  APIOrganSegUnifiedUploadFlow,
  APIOrganSegStartInstance,
  APIOrganSegHealthCheck,
  APIFileVisualization
} from '../helper/APIFunctions';
import { DEMO_ORGAN_KEY } from '../helper/DemoDataLoader';
import './OrganSegmentationPageV2.css';

/**
 * Organ Segmentation Page V2 - Redesigned UX
 * 
 * Sequential workflow:
 * 1. Demo viewer (default state)
 * 2. Upload popup
 * 3. Progress indicators
 * 4. Results with side-by-side viewers
 */
const OrganSegmentationPageV2 = () => {
  // Intro modal state
  const [showIntroModal, setShowIntroModal] = useState(true);
  const [introComplete, setIntroComplete] = useState(false);
  
  // UI State Management
  const [appState, setAppState] = useState('demo'); // demo, uploading, uploaded, segmenting, results, dicom-viewer
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showOrganModal, setShowOrganModal] = useState(false);
  const [viewerLayout, setViewerLayout] = useState('mesh-only'); // split, stack-only, mesh-only
  
  // Data State
  const [uploadedFile, setUploadedFile] = useState(null);
  const [modality, setModality] = useState(null); // Require user to select modality first
  const [selectedOrgan, setSelectedOrgan] = useState(null);
  const [organDisplayName, setOrganDisplayName] = useState(null);
  const [sliceInfo, setSliceInfo] = useState(null);
  const [segmentationStatus, setSegmentationStatus] = useState(null);
  
  // Upload State
  const [uiState, setUiState] = useState('ready');
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  
  // Processing State
  const [isSegmenting, setIsSegmenting] = useState(false);
  const [processingStage, setProcessingStage] = useState('idle');
  
  // DICOM Viewer State
  const [showDicomUploadModal, setShowDicomUploadModal] = useState(false);
  const [dicomViewerUrl, setDicomViewerUrl] = useState(null);
  const [dicomDragActive, setDicomDragActive] = useState(false);
  const [dicomUploading, setDicomUploading] = useState(false);

  // Intro animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIntroComplete(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Poll segmentation status
  useEffect(() => {
    if (!isSegmenting) return;

    const pollStatus = () => {
      APIOrganSegGetStatus(
        (data) => {
          setSegmentationStatus(data);
          if (data.current_organ) {
            setProcessingStage('segmenting');
          }
        },
      );
    };

    pollStatus();
    const interval = setInterval(pollStatus, 500);
    return () => clearInterval(interval);
  }, [isSegmenting]);

  const handleUploadClick = () => {
    setShowUploadModal(true);
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    
    // Validate file type first
    const validExtensions = ['.nii', '.nii.gz', '.dcm', '.zip'];
    const fileName = file.name.toLowerCase();
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType) {
      setError(`Invalid file type. Please upload: ${validExtensions.join(', ')}`);
      setUiState('ready');
      return;
    }
    
    setError(null);
    
    // Stage 1: Start server (if needed)
    setUiState('starting-server');
    setUploadProgress('Starting AI Server...');
    
    // Dispatch event to notify ServerStatus that server is starting
    window.dispatchEvent(new CustomEvent('serverStarting'));
    
    APIOrganSegStartInstance(
      (response) => {
        
        // If server was already running, proceed immediately
        // If server is starting, poll health check until ready
        const serverWasOffline = response?.message?.includes('start') || response?.state === 'pending';
        
        if (serverWasOffline) {
          // Server is starting, poll health check until it's ready
          setUploadProgress('Waiting for server to come online...');
          
          let pollAttempts = 0;
          const maxPolls = 60; // 5 minutes max (60 * 5 seconds)
          const pollInterval = setInterval(() => {
            pollAttempts++;
            const elapsedSeconds = pollAttempts * 5;
            setUploadProgress(`Waiting for server... (${elapsedSeconds}s)`);
            
            APIOrganSegHealthCheck(
              (healthResponse) => {
                if (healthResponse.status === 'healthy' || healthResponse.status === 'ok') {
                  clearInterval(pollInterval);
                  setUiState('uploading');
                  setUploadProgress('Server online, uploading file...');
                  proceedWithUpload(file);
                }
              },
              (healthError) => {
                
                if (pollAttempts >= maxPolls) {
                  clearInterval(pollInterval);
                  setError('Server took too long to start. Please try again or contact support.');
                  setUploadProgress(null);
                  setUiState('ready');
                }
              }
            );
          }, 5000); // Check every 5 seconds
        } else {
          // Server already online, proceed immediately
          setUiState('uploading');
          setUploadProgress('Uploading file...');
          proceedWithUpload(file);
        }
      },
      (err) => {
        setError(`Failed to start server: ${err}`);
        setUploadProgress(null);
        setUiState('ready');
      }
    );
  };
  
  const proceedWithUpload = (file) => {
    APIOrganSegUnifiedUploadFlow(
      file,
      modality,
      setUiState,
      (fileInfo) => {
        setUploadedFile(fileInfo);
        setModality(fileInfo.modality || 'ct');
        setError(null);
        setShowUploadModal(false);
        setShowOrganModal(true);
        setAppState('uploaded');
        setUploadProgress(null);
        setUiState('ready');
      },
          (err) => {
            let errorMessage = err;
            if (typeof err === 'string' && err.includes('backend may not support S3')) {
              errorMessage = 'Upload failed: The backend needs to be updated. Please contact support or rebuild the Docker image with the latest code.';
            }
            setError(errorMessage);
            setAppState('demo');
            setUploadProgress(null);
            setUiState('ready');
          }
    );
  };

  // DICOM Viewer Handlers
  const handleDicomUploadClick = () => {
    setShowDicomUploadModal(true);
  };

  const handleDicomFileUpload = (file) => {
    if (!file) return;
    
    // Validate file type
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.dcm', '.zip'];
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType) {
      setError(`Invalid file type for DICOM viewer. Please upload .dcm or .zip files.`);
      return;
    }
    
    setError(null);
    setDicomUploading(true);
    setUploadProgress('Uploading DICOM file...');
    
    // Use the same API as /upload page
    APIFileVisualization(
      file,
      fileName.split('.')[0], // Use filename as title
      'DICOM stack uploaded from Organ Segmentation page',
      (response) => {
        setDicomViewerUrl(response.visualization_url);
        setDicomUploading(false);
        setUploadProgress(null);
        setShowDicomUploadModal(false);
        setAppState('dicom-viewer');
      },
      (error) => {
        setError(`Failed to upload DICOM: ${error.message || error}`);
        setDicomUploading(false);
        setUploadProgress(null);
      },
      false, // isOrg = false
      {} // No additional args
    );
  };

  const handleDicomDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDicomDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleDicomFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDicomDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDicomDragActive(true);
  };

  const handleDicomDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDicomDragActive(false);
  };

  const handleBackToDemo = () => {
    setAppState('demo');
    setDicomViewerUrl(null);
    setError(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleOrganSubmit = async (organs) => {
    setShowOrganModal(false);
    setAppState('segmenting');
    setError(null);
    setIsSegmenting(true);
    
    const organsArray = Array.isArray(organs) ? organs : [organs];
    const organKey = organsArray.slice().sort().join('_');
    
    const displayName = organsArray.length === 1 
      ? organsArray[0] 
      : `${organsArray.length} organs (${organsArray.slice(0, 3).join(', ')}${organsArray.length > 3 ? '...' : ''})`;
    
    setSelectedOrgan(organKey);
    setOrganDisplayName(displayName);
    setProcessingStage('preprocessing');

    try {
      await new Promise(resolve => setTimeout(resolve, 200));
      setProcessingStage('segmenting');
      
      APIOrganSegSegment(
        organsArray,
        modality,
        true,
        async (data) => {
          if (data.success) {
            setProcessingStage('mesh');
            await new Promise(resolve => setTimeout(resolve, 800));
            
            APIOrganSegGetSlices(
              organKey,
              async (sliceData) => {
                setSliceInfo(sliceData);
                setProcessingStage('complete');
                await new Promise(resolve => setTimeout(resolve, 500));
                setIsSegmenting(false);
                setViewerLayout('split'); // Show both viewers after segmentation
                setAppState('results');
              },
              (error) => {
                setError(`Failed to fetch slices: ${error}`);
                setIsSegmenting(false);
                setAppState('demo');
              }
            );
          }
        },
        (error) => {
          setError(error || 'Segmentation failed');
          setIsSegmenting(false);
          setAppState('demo');
        }
      );
    } catch (err) {
      setError(err.message || 'Unexpected error occurred');
      setIsSegmenting(false);
      setAppState('demo');
    }
  };

  // Apply dark background to body when component mounts
  useEffect(() => {
    // Apply organ segmentation styles
    document.body.style.background = 'linear-gradient(135deg, #0f0a1e, #1a1430, #2d1b4e, #1e3a8a)';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.body.style.maxHeight = '100vh';
    
    // Cleanup: force reset to default values
    return () => {
      document.body.style.background = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.maxHeight = '';
    };
  }, []);

  return (
    <div className="organ-segmentation-page-v2">
      {/* Intro Modal */}
      {showIntroModal && (
        <>
          <div className="intro-modal-backdrop" onClick={() => {
            setShowIntroModal(false);
            setIntroComplete(true);
          }} />
          <div className="intro-modal-container">
            <div className="intro-modal-card">
              <div className="intro-modal-background">
                <div 
                  className="intro-modal-background-image"
                  style={{ backgroundImage: `url(${TotalSegmentatorImage})` }}
                ></div>
                <div className="intro-modal-background-overlay"></div>
              </div>
              <div className="intro-modal-content">
                <div className="intro-modal-icon">⚡</div>
                <h1 className="intro-modal-title">Scan DeepView</h1>
                <p className="intro-modal-subtitle">AI-powered 3D medical imaging visualization</p>
                
                <div className="intro-modal-disclaimer">
                  <p>
                    This software is provided for <strong>informational use only</strong> and is not for use in the diagnosis, cure, mitigation, treatment or prevention of any disease or medical condition.
                  </p>
                </div>
                
                <button 
                  className="intro-modal-button"
                  onClick={() => {
                    setShowIntroModal(false);
                    setIntroComplete(true);
                  }}
                >
                  I Understand
                </button>
                
                <p style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  marginTop: '16px',
                  marginBottom: '0',
                  textAlign: 'center',
                  opacity: 0.8
                }}>
                  This feature is best used on desktop. Mobile support is limited.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Intro loading animation */}
      {!introComplete && (
        <div className="intro-loading-overlay">
          <div className="thinking-loader"></div>
        </div>
      )}

      <Container fluid className="main-container" style={{ background: 'transparent' }}>
        {/* Disclaimer footer text */}
        <div className="disclaimer-footer">
          For informational use only. Not for diagnostic use.
        </div>

        {/* Server Status (draggable, minimal) */}
        <ServerStatus />

        {/* Standalone Segment Button (only shown in demo mode) */}
        {appState === 'demo' && (
          <button 
            className="standalone-segment-btn"
            onClick={handleUploadClick}
            title="Upload and segment medical imaging data"
          >
            <span className="segment-text">Segment Image Stack</span>
          </button>
        )}

        {/* Load Image Stack Button (below segment button) */}
        {appState === 'demo' && (
          <button 
            className="standalone-segment-btn"
            onClick={handleDicomUploadClick}
            title="Load and view DICOM imaging data"
            style={{
              top: 'calc(50% + 90px)',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              border: '2px solid rgba(59, 130, 246, 0.4)',
            }}
          >
            <span className="segment-text">Load Image Stack</span>
          </button>
        )}

        {/* Main Viewer Panel */}
        <div className="main-viewer-panel">
          {(appState === 'demo' || appState === 'uploaded') && (
            <>
              <div className="demo-overlay">
                <div className="demo-badge">
                  <span className="badge bg-info">Demo Dataset</span>
                </div>
              </div>
              
              <div className="viewer-grid">
                <div className={`viewer-section ${viewerLayout === 'stack-only' ? 'd-none' : viewerLayout === 'mesh-only' ? 'full-width' : ''}`} style={{ position: 'relative' }}>
                  <MeshViewer organ={DEMO_ORGAN_KEY} isDemo={true} />
                  <div style={{ position: 'absolute', bottom: '20px', right: '20px', zIndex: 10 }}>
                    <ReportInaccuraciesButton 
                      context="Organ Segmentation - 3D Viewer" 
                      variant="icon"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                        color: '#9ca3af',
                        fontSize: '20px',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)'
                      }}
                    />
                  </div>
                </div>
                {/* Hide Stack Viewer in demo mode - only showing mesh visualization */}
              </div>
            </>
          )}

          {appState === 'uploading' && (
            <div className="upload-progress-overlay">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">{uploadProgress || 'Uploading file...'}</p>
            </div>
          )}

          {appState === 'segmenting' && (
            <div className="segmentation-progress-overlay">
              <ProgressIndicator 
                stage={processingStage} 
                organ={organDisplayName}
                segmentationStatus={segmentationStatus}
              />
            </div>
          )}

          {appState === 'dicom-viewer' && dicomViewerUrl && (
            <div className="viewer-grid" style={{ position: 'relative' }}>
              <div className="viewer-section full-width" style={{ position: 'relative', height: '100%' }}>
                <iframe
                  src={dicomViewerUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#000'
                  }}
                  title="DICOM Viewer"
                  allow="fullscreen"
                />
                <div style={{ position: 'absolute', bottom: '20px', right: '20px', zIndex: 10, display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleBackToDemo}
                    style={{
                      background: 'rgba(59, 130, 246, 0.9)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.9)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    ← Back to Demo
                  </button>
                  <ReportInaccuraciesButton 
                    context="Organ Segmentation - DICOM Viewer" 
                    variant="icon"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      color: '#9ca3af',
                      fontSize: '20px',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {appState === 'results' && selectedOrgan && sliceInfo && (
            <div className="viewer-grid">
              <div className={`viewer-section ${viewerLayout === 'stack-only' ? 'd-none' : viewerLayout === 'mesh-only' ? 'full-width' : ''}`} style={{ position: 'relative' }}>
                <MeshViewer 
                  organ={selectedOrgan} 
                  fileType={uploadedFile?.file_type}
                  viewerLayout={viewerLayout}
                  onToggleLayout={() => setViewerLayout(viewerLayout === 'split' ? 'mesh-only' : 'split')}
                />
                <div style={{ position: 'absolute', bottom: '20px', right: '20px', zIndex: 10 }}>
                  <ReportInaccuraciesButton 
                    context="Organ Segmentation - 3D Viewer" 
                    variant="icon"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                      color: '#9ca3af',
                      fontSize: '20px',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)'
                    }}
                  />
                </div>
              </div>
              <div className={`viewer-section ${viewerLayout === 'mesh-only' ? 'd-none' : viewerLayout === 'stack-only' ? 'full-width' : ''}`}>
                <StackViewer organ={selectedOrgan} sliceInfo={sliceInfo} />
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="error-alert">
            <strong>Error:</strong> {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}
      </Container>

      {/* Upload Modal */}
      <Modal 
        show={showUploadModal} 
        onHide={() => setShowUploadModal(false)}
        size="lg"
        centered
        backdrop={true}
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>Upload Imaging Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Modality Selector */}
          <div className="modality-selector mb-4">
            <label className="form-label">Image Modality:</label>
            <div className="btn-group w-100" role="group">
              <input 
                type="radio" 
                className="btn-check" 
                name="modality" 
                id="modality-ct" 
                checked={modality === 'ct'}
                onChange={() => setModality('ct')}
              />
              <label className="btn btn-outline-primary" htmlFor="modality-ct">
                <div className="modality-option">
                  <span className="modality-label">CT Scan</span>
                  <small className="modality-count">72 organs</small>
                </div>
              </label>

              <input 
                type="radio" 
                className="btn-check" 
                name="modality" 
                id="modality-mri" 
                checked={modality === 'mri'}
                onChange={() => setModality('mri')}
              />
              <label className="btn btn-outline-primary" htmlFor="modality-mri">
                <div className="modality-option">
                  <span className="modality-label">MRI Scan</span>
                  <small className="modality-count">50 organs</small>
                </div>
              </label>
            </div>
          </div>

          {/* Drop Zone */}
          <div 
            className={`upload-dropzone ${dragActive && modality ? 'drag-active' : ''} ${(uiState === 'starting-server' || uiState === 'uploading') ? 'uploading' : ''} ${!modality ? 'disabled' : ''}`}
            onDragEnter={modality ? handleDrag : undefined}
            onDragLeave={modality ? handleDrag : undefined}
            onDragOver={modality ? handleDrag : undefined}
            onDrop={modality ? handleDrop : undefined}
          >
            {!modality ? (
              <div className="upload-prompt">
                <p className="upload-title">Please select CT or MRI above</p>
                <p className="upload-hint">Choose your imaging modality to continue</p>
              </div>
            ) : uiState === 'starting-server' ? (
              <div className="upload-status">
                <Spinner animation="border" variant="light" />
                <p className="mt-3 text-white">Starting AI Server...</p>
                <small className="text-white-50">This may take 2-3 minutes</small>
              </div>
            ) : uiState === 'uploading' ? (
              <div className="upload-status">
                <Spinner animation="border" variant="light" />
                <p className="mt-3 text-white">Uploading file...</p>
                {uploadProgress && <small className="text-white-50">{uploadProgress}</small>}
              </div>
            ) : error ? (
              <div className="upload-error">
                <div className="error-icon">⚠️</div>
                <p className="error-title">Upload Failed</p>
                <p className="error-message">{error}</p>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={() => {
                    setError(null);
                    setUiState('ready');
                  }}
                >
                  Try Again
                </Button>
              </div>
            ) : uploadedFile ? (
              <div className="upload-success">
                <div className="success-icon">✓</div>
                <p><strong>{uploadedFile.filename}</strong></p>
                <p className="text-muted">{uploadedFile.file_type}</p>
                {uploadedFile.info?.shape && (
                  <p className="file-info">Shape: {uploadedFile.info.shape.join(' × ')}</p>
                )}
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => {
                    setUploadedFile(null);
                    setError(null);
                    document.getElementById('file-input').click();
                  }}
                >
                  Change File
                </Button>
              </div>
            ) : (
              <div className="upload-prompt">
                <p className="upload-title">Drop your file here</p>
                <p className="upload-hint">or click to browse</p>
                <p className="supported-formats">
                  Supports: .nii, .nii.gz, .dcm, .zip (DICOM)
                </p>
                <Button 
                  variant="primary"
                  onClick={() => modality && document.getElementById('file-input').click()}
                  disabled={!modality}
                >
                  Browse Files
                </Button>
              </div>
            )}
            
            <input
              id="file-input"
              type="file"
              accept=".nii,.nii.gz,.dcm,.zip"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={!modality}
            />
          </div>

          {uploadedFile && (
            <div className="mt-3 text-center">
              <Button 
                variant="success" 
                size="lg"
                onClick={() => {
                  setShowUploadModal(false);
                  setShowOrganModal(true);
                }}
              >
                Continue to Organ Selection →
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Organ Selection Modal */}
      <Modal 
        show={showOrganModal} 
        onHide={() => setShowOrganModal(false)}
        size="xl"
        centered
        backdrop={true}
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>Select Organs to Segment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OrganInput
            onSubmit={handleOrganSubmit}
            isLoading={isSegmenting}
            disabled={isSegmenting}
            modality={modality}
          />
        </Modal.Body>
      </Modal>

      {/* DICOM Upload Modal */}
      <Modal 
        show={showDicomUploadModal} 
        onHide={() => !dicomUploading && setShowDicomUploadModal(false)}
        size="lg"
        centered
        backdrop={!dicomUploading}
      >
        <Modal.Header closeButton={!dicomUploading} className="modal-header-custom">
          <Modal.Title>Load DICOM Image Stack</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Upload a DICOM file or ZIP archive to view your medical imaging data in our 3D viewer.
          </p>
          
          {/* Drop Zone */}
          <div 
            className={`upload-dropzone ${dicomDragActive ? 'drag-active' : ''} ${dicomUploading ? 'uploading' : ''}`}
            onDragEnter={handleDicomDragOver}
            onDragLeave={handleDicomDragLeave}
            onDragOver={handleDicomDragOver}
            onDrop={handleDicomDrop}
            style={{ minHeight: '250px', cursor: dicomUploading ? 'not-allowed' : 'pointer' }}
            onClick={() => !dicomUploading && document.getElementById('dicom-file-input').click()}
          >
            {dicomUploading ? (
              <div className="upload-status">
                <Spinner animation="border" variant="light" />
                <p className="mt-3 text-white">Uploading DICOM file...</p>
                {uploadProgress && <small className="text-white-50">{uploadProgress}</small>}
              </div>
            ) : error ? (
              <div className="upload-error">
                <div className="error-icon">⚠️</div>
                <p className="error-title">Upload Failed</p>
                <p className="error-message">{error}</p>
                <Button 
                  variant="outline-light" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setError(null);
                  }}
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <div className="upload-prompt">
                <p className="upload-title">Drop DICOM file here</p>
                <p className="upload-hint">or click to browse</p>
                <small className="text-muted">Supports .dcm and .zip files</small>
              </div>
            )}
            <input
              type="file"
              accept=".dcm,.DCM,.zip"
              style={{ display: 'none' }}
              id="dicom-file-input"
              disabled={dicomUploading}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleDicomFileUpload(e.target.files[0]);
                }
              }}
            />
          </div>
          
          {!dicomUploading && (
            <div className="d-flex justify-content-center mt-3">
              <Button 
                variant="outline-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('dicom-file-input').click();
                }}
              >
                Browse Files
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OrganSegmentationPageV2;

